import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Calculator, ChartLineUp, CheckCircle, ArrowRight,
  Phone, Calendar, Shield, FileText, CurrencyDollar,
  Target, TrendUp, Warning, Lightbulb
} from "@phosphor-icons/react";

const PDGMPage = () => {
  const pdgmComponents = [
    {
      title: "Admission Source",
      description: "Community vs. Institutional admission affects base payment rates significantly.",
      icon: Target
    },
    {
      title: "Clinical Grouping",
      description: "Principal diagnosis determines clinical group - impacts reimbursement substantially.",
      icon: FileText
    },
    {
      title: "Functional Impairment",
      description: "OASIS functional items determine impairment level and payment adjustment.",
      icon: ChartLineUp
    },
    {
      title: "Comorbidity Adjustment",
      description: "Secondary diagnoses can increase payments through comorbidity adjustments.",
      icon: Calculator
    }
  ];

  const services = [
    "PDGM readiness assessment",
    "OASIS documentation optimization",
    "Clinical grouping accuracy review",
    "Coding and sequencing training",
    "Revenue cycle optimization",
    "LUPA prevention strategies",
    "Compliance monitoring",
    "Performance benchmarking"
  ];

  const benefits = [
    { stat: "15-25%", label: "Revenue Increase Potential" },
    { stat: "30%", label: "LUPA Reduction" },
    { stat: "95%", label: "Coding Accuracy" },
    { stat: "ROI", label: "Within 90 Days" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>PDGM Optimization for Home Health | PDGM Consulting | Noble Hospice Solutions</title>
        <meta name="description" content="Expert PDGM optimization services for home health agencies. Maximize reimbursement through improved OASIS accuracy, clinical grouping optimization, and revenue cycle management. Free assessment available." />
        <meta name="keywords" content="PDGM, PDGM optimization, home health PDGM, PDGM consulting, PDGM reimbursement, OASIS optimization, home health revenue cycle, PDGM training, clinical grouping home health" />
        <link rel="canonical" href="https://noblehospicesolutions.com/pdgm" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "PDGM Optimization Services",
            "provider": {
              "@type": "Organization",
              "name": "Noble Hospice Solutions",
              "url": "https://noblehospicesolutions.com"
            },
            "description": "Expert PDGM optimization services for home health agencies to maximize reimbursement.",
            "areaServed": "United States"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-green-900/30 to-slate-900" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-400 text-sm font-semibold mb-6">
              <CurrencyDollar weight="fill" className="w-4 h-4" />
              Revenue Optimization
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6">
              PDGM Optimization
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-400">
                Maximize Home Health Revenue
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              The Patient-Driven Groupings Model changed home health reimbursement. 
              Our experts help you optimize every component for maximum revenue.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Calculator weight="bold" className="w-5 h-5" />
                Free Revenue Assessment
              </Link>
              <a
                href="tel:+17183046214"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <Phone weight="bold" className="w-5 h-5" />
                Call (718) 304-6214
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Stats */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {benefits.map((item, i) => (
              <div key={i}>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-cyan-600 mb-1">
                  {item.stat}
                </div>
                <div className="text-slate-600 font-medium">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PDGM Components */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl text-slate-900 font-bold mb-4">
              Understanding PDGM Components
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              PDGM uses four key components to determine payment. We optimize each one to maximize your reimbursement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pdgmComponents.map((component, index) => (
              <motion.div
                key={component.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-green-500/50 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-cyan-600 flex items-center justify-center mb-4">
                  <component.icon weight="fill" className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-2">{component.title}</h3>
                <p className="text-slate-600 text-sm">{component.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-green-900/30">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-6">
                Our PDGM Optimization Services
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                We provide comprehensive PDGM consulting to help your home health agency 
                capture every dollar you've earned through accurate documentation and coding.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle weight="fill" className="w-5 h-5 text-green-400" />
                    <span className="text-slate-200">{service}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="font-display text-2xl text-white font-bold mb-6">
                Common PDGM Mistakes We Fix
              </h3>
              <div className="space-y-4">
                {[
                  "Incorrect principal diagnosis sequencing",
                  "Missing comorbidity documentation",
                  "OASIS functional scoring errors",
                  "Failure to capture admission source",
                  "Inadequate clinical documentation"
                ].map((mistake, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Warning weight="fill" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-200">{mistake}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-500 to-cyan-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white font-bold mb-6">
            Stop Leaving Money on the Table
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Get a free PDGM revenue assessment and discover how much more your agency could be earning.
          </p>
          <Link
            to="/schedule-demo"
            className="bg-white text-green-600 font-bold px-8 py-4 rounded-full hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
          >
            Get Free Revenue Assessment
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PDGMPage;
